import { useState } from 'react';
import { Heading } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { REASON_MAX_LENGTH, REASON_MIN_LENGTH } from '@complan/contracts';
import { AriaModalDialog } from '@/shared/a11y/AriaDialog';
import { Button } from '@/components/ui/button';
import { TextArea } from '@/features/initiative/intake/fields';

/**
 * The Cancel initiative confirmation (R9.6): a required, non-blank reason of 1
 * to 500 characters. On confirm the status becomes Cancelled; every other field,
 * association, milestone and dependency is retained unchanged (the dialog does
 * not restate that beyond the informational body text — the server enforces it).
 *
 * A true dialog (focus trap, focus restore, `Esc`) via {@link AriaModalDialog}.
 */
export function CancelInitiativeDialog({
  isOpen,
  isSubmitting,
  hasError,
  onConfirm,
  onClose,
}: {
  readonly isOpen: boolean;
  readonly isSubmitting: boolean;
  readonly hasError: boolean;
  readonly onConfirm: (reason: string) => void;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  const trimmedLength = reason.trim().length;
  const isValid = trimmedLength >= REASON_MIN_LENGTH && reason.length <= REASON_MAX_LENGTH;

  return (
    <AriaModalDialog
      variant="centre"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      aria-label={t('detail.cancelDialog.title')}
    >
      <Heading slot="title" className="text-[15px] font-semibold text-txt">
        {t('detail.cancelDialog.title')}
      </Heading>
      <p className="text-muted-foreground mt-2 text-[12.3px]">{t('detail.cancelDialog.body')}</p>

      {hasError ? (
        <p className="mt-2 text-[12px] text-[#8E2A1E]">{t('detail.cancelDialog.error')}</p>
      ) : null}

      <div className="mt-3 flex flex-col gap-1">
        <label htmlFor="cancel-reason" className="text-[11.5px] font-semibold text-txt">
          {t('detail.cancelDialog.reasonLabel')}
        </label>
        <TextArea
          id="cancel-reason"
          value={reason}
          maxLength={REASON_MAX_LENGTH}
          onChange={(event) => setReason(event.target.value)}
        />
        <p className="text-muted-foreground text-[11px]">{t('detail.cancelDialog.reasonHint')}</p>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
          {t('detail.cancelDialog.cancel')}
        </Button>
        <Button
          variant="destructive"
          size="sm"
          disabled={!isValid || isSubmitting}
          onClick={() => onConfirm(reason.trim())}
        >
          {isSubmitting ? t('detail.cancelDialog.confirming') : t('detail.cancelDialog.confirm')}
        </Button>
      </div>
    </AriaModalDialog>
  );
}
